import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheck, FaDownload, FaPlay, FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { SEO } from '../../components/SEO';
import { FaCheckCircle, FaChartBar, FaCalendarAlt, FaExchangeAlt, FaBuilding, FaUniversity, FaChartLine, FaBriefcase } from 'react-icons/fa';
import BrandText from '../../styles/brandStyles';

const PlaneacionControl = () => {
  const { translate } = useLanguage();
  const [activeSection, setActiveSection] = useState('overview');
  const [isSticky, setIsSticky] = useState(false);
  
  // Estados para controlar los carruseles
  const [activeCard1, setActiveCard1] = useState(0);
  const [activeCard2, setActiveCard2] = useState(0);
  const [activeCard3, setActiveCard3] = useState(0);

  // Datos para los carruseles
  const carouselData1 = [
    {
      image: "/images/modules/opus_m2/AVANCES DE OBRA.png",
      title: "Control de avances físicos",
      description: "Realiza un seguimiento detallado del avance físico de tu obra. Compara lo programado vs lo ejecutado y genera estimaciones automáticamente.",
      features: [
        "Registro de avances físicos por concepto",
        "Comparativo entre avance programado y real",
        "Análisis de desviaciones y tendencias"
      ],
      link: "/caracteristicas/control-avances"
    },
    {
      image: "/images/modules/opus_m2/EXPLOSION INSUMOS Y PROGRAMA DE SUMINISTROS.png",
      title: "Control integrado con BIM",
      description: "Integra tus modelos BIM con el control de avances para una visualización más precisa del progreso de obra.",
      features: [
        "Integración con modelos BIM",
        "Visualización 3D de avances",
        "Actualización en tiempo real"
      ],
      link: "/caracteristicas/control-bim"
    },
    {
      image: "/images/modules/opus_m2/PRESUPUESTO EJECUCION.png",
      title: "Control colaborativo",
      description: "Trabaja en equipo con múltiples usuarios en el mismo proyecto. Controla permisos y mantén un historial de cambios para una colaboración eficiente.",
      features: [
        "Trabajo simultáneo de múltiples usuarios",
        "Control de versiones y cambios",
        "Comentarios y anotaciones en tiempo real"
      ],
      link: "/caracteristicas/control-colaborativo"
    }
  ];

  const carouselData2 = [
    {
      image: "/images/modules/opus_m2/PRECIOS OFERTA PRECIOS EJECUCION.png",
      title: "Administra recursos de obra",
      description: "Gestiona eficientemente todos los recursos de tu proyecto: mano de obra, materiales, maquinaria y subcontratos.",
      features: [
        "Programación de recursos por período",
        "Control de mano de obra y equipo",
        "Histogramas de recursos"
      ],
      link: "/caracteristicas/recursos-obra"
    },
    {
      image: "/images/modules/opus_m2/insumos administracion directa.png",
      title: "Distribución de recursos",
      description: "Optimiza la asignación de recursos entre múltiples proyectos. Visualiza la carga de trabajo e identifica cuellos de botella.",
      features: [
        "Distribución inteligente de recursos",
        "Visualización de carga de trabajo",
        "Optimización de recursos disponibles"
      ],
      link: "/caracteristicas/distribucion-recursos"
    },
    {
      image: "/images/modules/opus_m2/SEGURIDAD.png",
      title: "Alertas y notificaciones",
      description: "Recibe alertas sobre sobreasignación de recursos, retrasos en actividades críticas y otros eventos importantes del proyecto.",
      features: [
        "Alertas de sobreasignación",
        "Notificaciones de retrasos",
        "Recordatorios de hitos importantes"
      ],
      link: "/caracteristicas/alertas-recursos"
    }
  ];

  const carouselData3 = [
    {
      image: "/images/modules/opus_m2/plantilla 4.png",
      title: "Análisis financiero de proyectos",
      description: "Evalúa el rendimiento financiero de tus proyectos con indicadores clave como valor ganado, CPI y SPI.",
      features: [
        "Análisis de valor ganado (EVM)",
        "Índices de desempeño de costos y cronograma",
        "Proyecciones financieras"
      ],
      link: "/caracteristicas/analisis-financiero"
    },
    {
      image: "/images/modules/opus_m2/pagos.png",
      title: "Estimaciones y pagos",
      description: "Genera estimaciones parciales y acumulativas. Controla el proceso de autorización, facturación y pago.",
      features: [
        "Estimaciones parciales y acumulativas",
        "Control de pagos y facturación",
        "Retenciones y descuentos"
      ],
      link: "/caracteristicas/estimaciones-pagos"
    },
    {
      image: "/images/modules/opus_m2/valor ganado2.png",
      title: "Flujo de efectivo",
      description: "Proyecta el flujo de efectivo de tus proyectos para anticipar necesidades de financiamiento y optimizar la gestión de recursos.",
      features: [
        "Proyección de flujo de efectivo",
        "Análisis de escenarios financieros",
        "Optimización de capital de trabajo"
      ],
      link: "/caracteristicas/flujo-efectivo"
    }
  ];

  // Función para manejar el scroll y actualizar la sección activa
  useEffect(() => {
    const handleScroll = () => {
      // Detectar si el submenú debe ser sticky
      const heroSection = document.getElementById('overview');
      const headerHeight = 64; // Altura del header principal
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      } else {
        // Si no se encuentra la sección, mantener el menú sticky para evitar que desaparezca
        setIsSticky(true);
      }

      // Detectar qué sección está visible
      const sections = ['overview', 'features', 'capabilities', 'benefits', 'cta'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140; // Altura del header + submenu
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      // Si no se encuentra ninguna sección activa, mantener la última activa
      if (!activeFound && window.scrollY > 0) {
        // Mantener la sección activa anterior o establecer una por defecto
        setIsSticky(true);
      }
    };

    // Ejecutar handleScroll inmediatamente para establecer el estado inicial
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Función para hacer scroll a una sección
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140; // Altura del header + submenu
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Funciones para navegar entre las tarjetas
  const nextCard = (setActiveCard, activeCard, maxCards) => {
    setActiveCard((activeCard + 1) % maxCards);
  };

  const prevCard = (setActiveCard, activeCard, maxCards) => {
    setActiveCard((activeCard - 1 + maxCards) % maxCards);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="OPUS - Módulo 2: Planeación y Control Integral | ECOSOFT"
        description="Administre la ejecución de obra, control de avances y recursos con el módulo de Planeación y Control Integral de OPUS."
        keywords="OPUS, planeación, control de obra, avances, estimaciones, ingeniería de costos"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]" style={{
        backgroundImage: 'url("/images/modules/opus_m2/cabecera_m2.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <div className="flex items-center mb-6">
              <div className="relative w-32 h-32 mr-6 flex-shrink-0">
                <div className="absolute inset-[5%] bg-white rounded-full"></div>
                <img 
                  src="/images/m2.svg" 
                  alt="Módulo 2" 
                  className="w-full h-full relative z-10"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-5xl font-bold">OPUS M2</h1>
                <h2 className="text-4xl font-bold mt-2">Planeación y Control</h2>
              </div>
            </div>
            <p className="text-xl mb-8">
              Control integral de proyectos, programación de obra y seguimiento de avances físicos y financieros.
            </p>
            <div className="flex gap-4">
              <Link to="/soporte/descargas" className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors">
                Descargar
              </Link>
              <Link to="/Distribuidores" className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors">
                Ver demostración
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Submenu Navigation */}
      <div 
        className={`bg-white shadow-md transition-all duration-300 sticky-nav ${
          isSticky 
            ? 'fixed top-[64px] left-0 right-0 z-30 border-t border-gray-200' 
            : 'relative'
        }`}
        style={{ minHeight: '56px' }} // Altura mínima para evitar saltos
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex overflow-x-auto py-4 items-center relative">
            <div className="flex-grow flex">
              <button 
                onClick={() => scrollToSection('overview')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'overview' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Resumen
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'features' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Características
              </button>
              <button 
                onClick={() => scrollToSection('capabilities')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'capabilities' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Capacidades
              </button>
              <button 
                onClick={() => scrollToSection('benefits')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'benefits' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Beneficios
              </button>
              <button 
                onClick={() => scrollToSection('cta')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'cta' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Comenzar
              </button>
            </div>
            <div className="flex items-center ml-4">
              <img 
                src="/images/m2.svg" 
                alt="Módulo 2 - Planeación y Control" 
                className="h-12 w-auto"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <section id="features" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Características principales</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartBar className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Control de avances</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Registro de avances físicos por concepto y período</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Comparativo entre avance programado y real</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Análisis de desviaciones y tendencias</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Generación automática de estimaciones</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Fotografías y evidencias de avance</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCalendarAlt className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Administración de recursos</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Programación de recursos por período</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Control de mano de obra y equipo</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Distribución de recursos en múltiples proyectos</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Histogramas de recursos</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Alertas de sobreasignación</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaExchangeAlt className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Gestión financiera</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Estimaciones parciales y acumulativas</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Control de pagos y facturación</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Proyección de flujo de efectivo</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Análisis de valor ganado (EVM)</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Reportes financieros automatizados</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section - Similar to "What you can do with Autodesk Revit" */}
      <section id="capabilities" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-3">Lo que puedes hacer con OPUS M2</h2>
          <p className="text-xl text-gray-600 text-center mb-10 max-w-3xl mx-auto">
            Descubre cómo OPUS M2 te ayuda a controlar la ejecución de obras y administrar recursos de manera eficiente.
          </p>
          
          <div className="space-y-12">
            {/* Capability 1 */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-full md:w-1/2 relative group">
                <img 
                  src={carouselData1[activeCard1].image} 
                  alt={carouselData1[activeCard1].title} 
                  className="rounded-lg shadow-lg w-full h-72 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => prevCard(setActiveCard1, activeCard1, carouselData1.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Anterior"
                  >
                    <FaChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={() => nextCard(setActiveCard1, activeCard1, carouselData1.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Siguiente"
                  >
                    <FaChevronRight size={20} />
                  </button>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3 text-[#1A2B39]">{carouselData1[activeCard1].title}</h3>
                <p className="text-gray-600 mb-4">{carouselData1[activeCard1].description}</p>
                <ul className="space-y-2 mb-6">
                  {carouselData1[activeCard1].features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Capability 2 */}
            <div className="flex flex-col md:flex-row-reverse gap-6 items-center">
              <div className="w-full md:w-1/2 relative group">
                <img 
                  src={carouselData2[activeCard2].image} 
                  alt={carouselData2[activeCard2].title} 
                  className="rounded-lg shadow-lg w-full h-72 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => prevCard(setActiveCard2, activeCard2, carouselData2.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Anterior"
                  >
                    <FaChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={() => nextCard(setActiveCard2, activeCard2, carouselData2.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Siguiente"
                  >
                    <FaChevronRight size={20} />
                  </button>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3 text-[#1A2B39]">{carouselData2[activeCard2].title}</h3>
                <p className="text-gray-600 mb-4">{carouselData2[activeCard2].description}</p>
                <ul className="space-y-2 mb-6">
                  {carouselData2[activeCard2].features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Capability 3 */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-full md:w-1/2 relative group">
                <img 
                  src={carouselData3[activeCard3].image} 
                  alt={carouselData3[activeCard3].title} 
                  className="rounded-lg shadow-lg w-full h-72 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => prevCard(setActiveCard3, activeCard3, carouselData3.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Anterior"
                  >
                    <FaChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={() => nextCard(setActiveCard3, activeCard3, carouselData3.length)}
                    className="bg-white/90 hover:bg-white text-[#0066CC] p-3 rounded-full shadow-lg transition-colors"
                    aria-label="Siguiente"
                  >
                    <FaChevronRight size={20} />
                  </button>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3 text-[#1A2B39]">{carouselData3[activeCard3].title}</h3>
                <p className="text-gray-600 mb-4">{carouselData3[activeCard3].description}</p>
                <ul className="space-y-2 mb-6">
                  {carouselData3[activeCard3].features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section id="benefits" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Beneficios clave</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <FaChartBar className="text-[#0066CC] text-4xl mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[#1A2B39]">Mayor control de proyectos</h3>
              <p className="text-gray-600">
                Monitorea en tiempo real el avance de tus proyectos y toma decisiones informadas para mantenerlos en curso.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <FaCalendarAlt className="text-[#0066CC] text-4xl mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[#1A2B39]">Optimización de recursos</h3>
              <p className="text-gray-600">
                Maximiza el rendimiento de tus recursos mediante una asignación eficiente basada en necesidades reales.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <FaExchangeAlt className="text-[#0066CC] text-4xl mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[#1A2B39]">Mayor rentabilidad</h3>
              <p className="text-gray-600">
                Identifica desviaciones de costos a tiempo y toma acciones correctivas para mantener la rentabilidad del proyecto.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <FaBuilding className="text-[#0066CC] text-4xl mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[#1A2B39]">Mejora en la toma de decisiones</h3>
              <p className="text-gray-600">
                Accede a información precisa y actualizada para tomar decisiones estratégicas con confianza.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Integration with other modules */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1A2B39]">Integración con otros módulos</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            El Módulo 2 se integra perfectamente con el resto de OPUS para ofrecer una solución completa
          </p>
          
          <div className="grid md:grid-cols-5 gap-6 max-w-5xl mx-auto">
            <Link to="/modulos/presupuesto-programable" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 1</h3>
              <p className="text-gray-600 text-sm">Presupuesto Programable</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/modulos/planeacion-control" className="bg-[#F5F7FA] p-5 rounded-lg text-center border-2 border-[#0066CC] hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 2</h3>
              <p className="text-gray-600 text-sm">Planeación y Control</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/modulos/administracion-compras" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 3</h3>
              <p className="text-gray-600 text-sm">Administración de Compras</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/modulos/dependencias" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 4</h3>
              <p className="text-gray-600 text-sm">Dependencias</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/modulos/cuantificacion-bim" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 5</h3>
              <p className="text-gray-600 text-sm">Cuantificación BIM</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Cursos y CERTIFICADOS */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1A2B39]">Cursos y Certificados</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Fortalece tus conocimientos en OPUS Planeación y Control con nuestro programa de certificación
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Curso básico</h3>
              <p className="text-gray-600 mb-4 text-center">
                Aprende los fundamentos para controlar y monitorear la ejecución de proyectos
              </p>
              <Link 
                to="/certificaciones" 
                className="block text-center px-6 py-2 bg-[#0066CC] text-white rounded-md hover:bg-[#0055AA] transition-colors mx-auto w-full"
              >
                Ver detalles
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCalendarAlt className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Curso avanzado</h3>
              <p className="text-gray-600 mb-4 text-center">
                Domina técnicas avanzadas de planeación y control de recursos en obra
              </p>
              <Link 
                to="/certificaciones" 
                className="block text-center px-6 py-2 bg-[#0066CC] text-white rounded-md hover:bg-[#0055AA] transition-colors mx-auto w-full"
              >
                Ver detalles
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBriefcase className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Certificación oficial</h3>
              <p className="text-gray-600 mb-4 text-center">
                Obtén la certificación oficial ECOSOFT que valida tus conocimientos profesionales
              </p>
              <Link 
                to="/certificaciones" 
                className="block text-center px-6 py-2 bg-[#0066CC] text-white rounded-md hover:bg-[#0055AA] transition-colors mx-auto w-full"
              >
                Ver detalles
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" className="py-16 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para llevar el control de tus proyectos al siguiente nivel?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Descubre todo el potencial de OPUS M2 Planeación y Control Integral para optimizar la ejecución de tus proyectos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/soporte/descargas" 
              className="px-8 py-3 bg-[#0066CC] hover:bg-[#0088FF] text-white font-medium rounded-md transition-colors"
            >
              Descargar
            </Link>
            <Link 
              to="/Distribuidores" 
              className="px-8 py-3 border border-white hover:bg-white hover:text-[#1A2B39] text-white font-medium rounded-md transition-colors"
            >
              Solicitar demostración
            </Link>
            <Link 
              to="/Distribuidores" 
              className="px-8 py-3 bg-white text-[#1A2B39] hover:bg-gray-100 font-medium rounded-md transition-colors"
            >
              Contactar con ventas
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlaneacionControl; 